import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import StoredJsonDataService from '../../Services/StoredJsonDataService';
import ButtonColorsEnum from '../../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../../Enums/ButtonTypeEnum';
import IconPositionEnum from '../../Enums/IconPositionEnum';
import InputTypesEnum from '../../Enums/InputTypesEnum';
import ErrorMessage from '../ErrorMessage';
import FaIcon from '../FaIcon';
import FigmaButton from '../FigmaButton';
import InputWithClear from '../InputComponents/InputWithClear';
import Loading from '../loading';
import '../../Css/SavedFilterWindow.css';

/**
 * The filter panel's Save filter and Load filter window (CD-5726): the saved filters of the panel's search type, and in
 * Save mode the name to save the current filter under. A design-system modal portalled to `<body>`, above the filter
 * sheet and the detail popups.
 */
export default function SavedFilterWindow({
  mode,
  saveType,
  itemToSave = null,
  onClose,
  onLoad = () => {},
}: {
  mode: 'save' | 'load';
  saveType: string;
  itemToSave?: unknown;
  onClose: () => void;
  onLoad?: (item: unknown) => void;
}) {
  const [names, setNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  // While the overwrite question is open, Escape and Enter belong to it.
  const confirming = useRef(false);

  const isSave = mode === 'save';
  const selectedName = name.trim();

  const loadNames = () => {
    setLoading(true);
    StoredJsonDataService.getExistingNames(saveType)
      .then((result) => {
        setNames(result.names ?? []);
        setLoading(false);
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('SFW50');
        setLoading(false);
      });
  };

  useEffect(() => {
    loadNames();
  }, []);

  useEffect(() => {
    // Close only this window: the filter panel closes itself on Escape through a document listener, and this
    // capture-phase listener runs before it.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || confirming.current) {
        return;
      }
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  const save = () => {
    setLoading(true);
    StoredJsonDataService.postStoredJsonData(selectedName, saveType, itemToSave)
      .then((result) => {
        // A server exception comes back as HTTP 200 with an error text (FRONTEND.md).
        if (result?.error) {
          ErrorMessage.SetErrorMessage(result.error);
          setLoading(false);
          return;
        }
        onClose();
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('SFW86');
        setLoading(false);
      });
  };

  const open = (nameToOpen: string) => {
    setLoading(true);
    StoredJsonDataService.getDataByNameAndType(nameToOpen, saveType)
      .then((result) => onLoad(result))
      .catch(() => {
        ErrorMessage.SetErrorMessage('SFW96');
        setLoading(false);
      });
  };

  const deleteSelected = () => {
    setLoading(true);
    StoredJsonDataService.deleteDataByNameAndType(selectedName, saveType)
      .then((result) => {
        if (result?.error) {
          ErrorMessage.SetErrorMessage(result.error);
        }
        setName('');
        loadNames();
      })
      .catch(() => {
        ErrorMessage.SetErrorMessage('SFW112');
        setLoading(false);
      });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedName || confirming.current) {
      return;
    }
    if (!isSave) {
      open(selectedName);
      return;
    }
    if (!names.includes(selectedName)) {
      save();
      return;
    }
    confirming.current = true;
    ErrorMessage.SetApprove(
      `A saved filter named "${selectedName}" already exists.`,
      () => {
        confirming.current = false;
        save();
      },
      'Overwrite the saved filter?',
      () => {
        confirming.current = false;
      }
    );
  };

  const renderList = () => (
    <div className='col-12 sfw-list-cell'>
      <div className='sfw-list-head'>
        <span className='sfw-lbl'>Saved filters</span>
        {!isSave && (
          <button
            type='button'
            className='icon-btn sfw-delete'
            title='Delete the selected filter'
            aria-label='Delete the selected filter'
            disabled={!selectedName}
            onClick={deleteSelected}
          >
            <i className='fa-solid fa-trash-can' />
          </button>
        )}
      </div>
      {names.length === 0 ? (
        <div className='sfw-empty'>No saved filters yet.</div>
      ) : (
        <ul className='sfw-list'>
          {names.map((savedName) => (
            <li key={savedName}>
              <button
                type='button'
                className={`sfw-item${savedName === selectedName ? ' selected' : ''}`}
                aria-pressed={savedName === selectedName}
                onClick={() => setName(savedName)}
                onDoubleClick={isSave ? undefined : () => open(savedName)}
              >
                {savedName}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderNameField = () => (
    <div className='col-12'>
      <InputWithClear
        fieldName='Name'
        labelText='Name'
        type={InputTypesEnum.Text}
        value={name}
        onChange={(fieldName: string, newValue: string) => setName(newValue ?? '')}
        isInOneRow={true}
        margin='m-0'
      />
    </div>
  );

  const renderContent = () => (
    <form
      className='sfw-form'
      onSubmit={submit}
    >
      <div className='EditPanelInput'>
        <div className='container-fluid'>
          <div className='row'>
            {renderList()}
            {isSave && renderNameField()}
          </div>
        </div>
      </div>
      <div className='EditBoxFooter'>
        <div className='EditBoxFooterRight'>
          <FigmaButton
            label='Cancel'
            iconName='fa-solid fa-xmark'
            iconPosition={IconPositionEnum.Left}
            color={ButtonColorsEnum.Common}
            type={ButtonTypeEnum.NormalNormal}
            onClick={onClose}
          />
          <FigmaButton
            label={isSave ? 'Save' : 'Open'}
            iconName={isSave ? 'fa-solid fa-save' : 'fa-solid fa-folder-open'}
            iconPosition={IconPositionEnum.Left}
            color={ButtonColorsEnum.Primary}
            type={ButtonTypeEnum.NormalNormal}
            submit={true}
            disabled={!selectedName}
          />
        </div>
      </div>
    </form>
  );

  return createPortal(
    // The portal is outside the panel's DOM but inside its React tree, so clicks would still bubble up to whatever
    // hosts the panel — stop them here.
    <div
      className='bravo-ds'
      data-theme={document.documentElement.getAttribute('data-theme') || undefined}
      onClick={(e) => e.stopPropagation()}
    >
      <div className='modal-wrap sfw-wrap'>
        <div
          className='modal sfw-modal'
          role='dialog'
          aria-modal='true'
          aria-label={isSave ? 'Save filter' : 'Load filter'}
        >
          <div className='modal-head'>
            <span className='mh-icon'>
              <FaIcon iconName={isSave ? 'fa-solid fa-save' : 'fa-solid fa-folder-open'} />
            </span>
            <h2>{isSave ? 'Save filter' : 'Load filter'}</h2>
            <span className='sp' />
            <button
              type='button'
              className='close'
              title='Cancel'
              onClick={onClose}
            >
              <FaIcon iconName='fa-solid fa-xmark' />
            </button>
          </div>
          {loading ? (
            <div className='sfw-loading'>
              <Loading />
            </div>
          ) : (
            renderContent()
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
